import { useState, useEffect } from "react";
import { DashboardLayout } from "@/components/layout/DashboardLayout";
import { StatCard } from "@/components/dashboard/StatCard";
import { QuickActionCard } from "@/components/dashboard/QuickActionCard";
import { RecentMessages } from "@/components/dashboard/RecentMessages";
import { 
  FolderOpen, 
  MessageSquare, 
  Layers,
  Tag
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/components/ui/use-toast";
import { createClient } from '@supabase/supabase-js';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Mail, Trash2 } from "lucide-react";

// Okunmuş mesajları hatırlamak için yerel depolama anahtarı
const READ_MESSAGES_STORAGE_KEY = 'contact_messages_read_status';

// Supabase bağlantısı için gerekli bilgiler (tip hatalarını önlemek için)
const SUPABASE_URL = "https://zvmixzvxrkjyoussyuuq.supabase.co";
const SUPABASE_PUBLISHABLE_KEY = "eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6Inp2bWl4enZ4cmtqeW91c3N5dXVxIiwicm9sZSI6ImFub24iLCJpYXQiOjE3NDY3MTQ2ODEsImV4cCI6MjA2MjI5MDY4MX0.i2KWdzVLx_zFqteiX7SVy-TdnaoEhM--Je4_9ocX9fs";

// Mesaj veri yapısı
interface Message {
  id: number;
  name: string;
  surname: string;
  email: string;
  phone?: string;
  message: string;
  web3forms_submission_id?: string;
  created_at: Date;
  read: boolean;
}

export default function Dashboard() {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [stats, setStats] = useState({
    totalProjects: "0",
    totalCategories: "0"
  });
  const [loading, setLoading] = useState(true);
  const [recentMessages, setRecentMessages] = useState<Message[]>([]);
  const [useDbReadStatus, setUseDbReadStatus] = useState(false); // Veritabanında read sütunu var mı?
  const [selectedMessage, setSelectedMessage] = useState<Message | null>(null);
  
  // Yerel depolamadan okunmuş mesajları al
  const getReadMessageIds = (): number[] => {
    try {
      const savedIds = localStorage.getItem(READ_MESSAGES_STORAGE_KEY);
      return savedIds ? JSON.parse(savedIds) : [];
    } catch (error) {
      console.error('Okunmuş mesaj bilgisi alınamadı:', error);
      return [];
    }
  };
  
  // Veritabanında read sütununun varlığını kontrol et
  const checkReadColumnExists = async () => {
    try {
      const supabaseClient = createClient(SUPABASE_URL, SUPABASE_PUBLISHABLE_KEY);
      
      // Basit bir test: Herhangi bir mesajın read değerini alıp başarılı olursa sütun var demektir
      const { data, error } = await (supabaseClient as any)
        .from('contact_messages')
        .select('read')
        .limit(1);
      
      if (!error) {
        setUseDbReadStatus(true);
        console.log("Read sütunu mevcut, veritabanı güncellemeleri aktif.");
      } else {
        console.log("Read sütunu bulunamadı, yerel depolama kullanılacak.");
        setUseDbReadStatus(false);
      }
    } catch (error) {
      console.error("Read sütunu kontrolü sırasında hata:", error);
      setUseDbReadStatus(false);
    }
  };
  
  // Verileri Supabase'den çekiyoruz
  useEffect(() => {
    async function fetchData() {
      try {
        setLoading(true);
        
        // Önce read sütununun var olup olmadığını kontrol et
        await checkReadColumnExists();
        
        // Toplam proje sayısını al
        const { count: projectsCount, error: projectsError } = await supabase
          .from('projects')
          .select('*', { count: 'exact', head: true });
        
        if (projectsError) {
          console.error('Proje sayısı alınırken hata:', projectsError);
          toast({ title: "Veri Yükleme Hatası", description: projectsError.message, variant: "destructive" });
        }
        
        // Toplam kategori sayısını al
        const { count: categoriesCount, error: categoriesError } = await supabase
          .from('categories')
          .select('*', { count: 'exact', head: true });
        
        if (categoriesError) {
          console.error('Kategori sayısı alınırken hata:', categoriesError);
          toast({ title: "Veri Yükleme Hatası", description: categoriesError.message, variant: "destructive" });
        }
        
        // Son mesajları çek
        try {
          // Supabase tiplerini bypass etmek için createClient kullanıyoruz
          const supabaseClient = createClient(SUPABASE_URL, SUPABASE_PUBLISHABLE_KEY);
          
          // TypeScript tiplerini bypass etmek için özel tip kullanıyoruz
          const { data: messageData, error: messagesError } = await (supabaseClient as any)
            .from('contact_messages')
            .select('*')
            .order('created_at', { ascending: false })
            .limit(3);
          
          if (messagesError) {
            throw messagesError;
          }
          
          if (messageData) {
            // Okunmuş mesaj ID'lerini al (yerel depolamadan)
            const readMessageIds = getReadMessageIds();
            
            // Mesajları dönüştür ve özetleri oluştur
            const formattedMessages = messageData.map((msg: any) => ({
              id: msg.id,
              name: msg.name,
              surname: msg.surname,
              email: msg.email,
              phone: msg.phone,
              message: msg.message || "",
              web3forms_submission_id: msg.web3forms_submission_id,
              excerpt: msg.message ? 
                (msg.message.length > 100 ? msg.message.substring(0, 100) + '...' : msg.message) : 
                "Mesaj içeriği yok",
              created_at: new Date(msg.created_at),
              // Veritabanında read sütunu varsa oradan al, yoksa yerel depolamadan kontrol et
              read: useDbReadStatus ? Boolean(msg.read) : readMessageIds.includes(msg.id)
            }));
            setRecentMessages(formattedMessages);
          }
        } catch (error) {
          console.error('Son mesajlar çekilirken hata:', error);
          // Hata olsa bile devam et, kritik değil
        }
        
        // Verileri güncelle
        setStats({
          totalProjects: String(projectsCount || 0),
          totalCategories: String(categoriesCount || 0)
        });
      } catch (error) {
        console.error('Veri çekme hatası:', error);
        toast({ 
          title: "Veri Yükleme Hatası", 
          description: "İstatistikler yüklenirken bir hata oluştu.", 
          variant: "destructive" 
        });
      } finally {
        setLoading(false);
      }
    }
    
    fetchData();
  }, [toast]);
  
  const quickActions = [
    { 
      title: "İçerik Aksiyonları",
      actions: [
        { label: "Yeni Slayt", href: "/content/home/slides/new" },
        { label: "Yeni Proje", href: "/portfolio/projects/new" },
        { label: "Hizmet Ekle", href: "/content/home/services/new" }
      ]
    },
    {
      title: "Portföy Aksiyonları",
      actions: [
        { label: "Kategori Ekle", href: "/portfolio/categories/new" },
        { label: "Proje Ekle", href: "/portfolio/projects/new" }
      ]
    }
  ];
  
  // Mesajı görüntüle ve okundu olarak işaretle
  const handleViewMessage = async (id: number) => {
    const message = recentMessages.find(msg => msg.id === id);
    if (!message) return;
    
    setSelectedMessage({...message, read: true});
    
    if (!message.read) {
      try {
        // Supabase istemcisini doğrudan oluştur
        const supabaseClient = createClient(SUPABASE_URL, SUPABASE_PUBLISHABLE_KEY);
        
        const { error } = await (supabaseClient as any)
          .from('contact_messages')
          .update({ read: true })
          .eq('id', message.id);
        
        if (error) {
          throw error;
        }
        
        // Mesajları güncelle
        setRecentMessages(prev => 
          prev.map(msg => msg.id === message.id ? {...msg, read: true} : msg)
        );
      } catch (error) {
        console.error('Mesaj okundu olarak işaretlenirken hata:', error);
        toast({
          title: "Hata",
          description: "Mesaj durumu güncellenirken bir sorun oluştu.",
          variant: "destructive"
        });
      }
    }
  };
  
  // Mesajı sil
  const deleteMessage = async (id: number) => {
    if (window.confirm("Bu mesajı silmek istediğinize emin misiniz?")) {
      try {
        // Supabase istemcisini doğrudan oluştur
        const supabaseClient = createClient(SUPABASE_URL, SUPABASE_PUBLISHABLE_KEY);
        
        const { error } = await (supabaseClient as any)
          .from('contact_messages')
          .delete()
          .eq('id', id);
        
        if (error) {
          throw error;
        }
        
        // Mesajları güncelle
        setRecentMessages(prev => prev.filter(msg => msg.id !== id));
        
        // Eğer açık olan mesaj siliniyorsa, modalı kapat
        if (selectedMessage && selectedMessage.id === id) {
          setSelectedMessage(null);
        }
        
        toast({
          title: "Mesaj silindi",
          description: "Mesaj başarıyla silindi."
        });
      } catch (error) {
        console.error('Mesaj silinirken hata:', error);
        toast({
          title: "Hata",
          description: "Mesaj silinirken bir sorun oluştu.",
          variant: "destructive"
        });
      }
    }
  };
  
  const closeDialog = () => {
    setSelectedMessage(null);
  };
  
  const handleReply = (messageId: number) => {
    // This would open an email client or a reply form in a real application
    window.open(`mailto:${selectedMessage?.email}?subject=Yanıt: İletişim Formu Mesajı`);
  };

  return (
    <DashboardLayout>
      <div className="space-y-6">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Dashboard</h1>
          <p className="text-muted-foreground">Yönetim paneli genel bakış</p>
        </div>
        
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-2">
          <StatCard
            title="Toplam Kategori"
            value={stats.totalCategories}
            icon={<Tag className="h-4 w-4" />}
          />
          <StatCard
            title="Toplam Proje"
            value={stats.totalProjects}
            icon={<Layers className="h-4 w-4" />}
          />
        </div>
        
        <div className="grid gap-4 md:grid-cols-2">
          {quickActions.map((action, index) => (
            <QuickActionCard
              key={index}
              title={action.title}
              actions={action.actions}
            />
          ))}
        </div>
        
        <div className="grid gap-4 md:grid-cols-1 lg:grid-cols-3">
          <RecentMessages 
            messages={recentMessages} 
            onViewMessage={handleViewMessage} 
          />
        </div>
      </div>
      
      <Dialog open={!!selectedMessage} onOpenChange={closeDialog}>
        <DialogContent className="sm:max-w-[600px]">
          <DialogHeader>
            <DialogTitle>İletişim Formu Mesajı</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="grid grid-cols-4 gap-2 text-sm">
              <span className="text-muted-foreground">Gönderen:</span>
              <span className="col-span-3 font-medium">{selectedMessage?.name} {selectedMessage?.surname}</span>
              
              <span className="text-muted-foreground">Email:</span>
              <span className="col-span-3">{selectedMessage?.email}</span>
              
              {selectedMessage?.phone && (
                <>
                  <span className="text-muted-foreground">Telefon:</span>
                  <span className="col-span-3">{selectedMessage?.phone}</span>
                </>
              )}
              
              <span className="text-muted-foreground">Tarih:</span>
              <span className="col-span-3">
                {selectedMessage?.created_at.toLocaleDateString('tr-TR', {
                  day: '2-digit',
                  month: '2-digit',
                  year: 'numeric',
                  hour: '2-digit',
                  minute: '2-digit'
                })}
              </span>
              
              {selectedMessage?.web3forms_submission_id && (
                <>
                  <span className="text-muted-foreground">Form ID:</span>
                  <span className="col-span-3">{selectedMessage?.web3forms_submission_id}</span>
                </>
              )}
            </div>
            
            <div className="rounded-md border p-4 mt-4">
              <p className="whitespace-pre-line">{selectedMessage?.message}</p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={closeDialog}>
              Kapat
            </Button>
            <Button variant="destructive" onClick={() => selectedMessage && deleteMessage(selectedMessage.id)}>
              <Trash2 className="mr-2 h-4 w-4" />
              Sil
            </Button>
            <Button onClick={() => handleReply(selectedMessage?.id || 0)}>
              <Mail className="mr-2 h-4 w-4" />
              Yanıtla
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </DashboardLayout>
  );
}
